import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Building2, GitBranch, CalendarRange } from 'lucide-react';
import { erpList, erpCreate } from '../../../services/erpApi';
import { Card, Button, Input, Select, PageHeader, Segmented, Table, FormModal, EmptyState, Badge, fmtDate } from '../ui';

const TABS = {
  companies: { label: 'Companies', icon: Building2, path: 'companies', parent: false, singular: 'Company',
    fields: [{ k: 'name', label: 'Company name', placeholder: 'Namoh Toolings Pvt Ltd' }, { k: 'gstin', label: 'GSTIN', placeholder: '27AAAAA0000A1Z5' }, { k: 'state', label: 'State', placeholder: 'Maharashtra' }] },
  branches: { label: 'Branches', icon: GitBranch, path: 'branches', parent: true, singular: 'Branch',
    fields: [{ k: 'name', label: 'Branch name', placeholder: 'Pune Warehouse' }, { k: 'state', label: 'State', placeholder: 'Maharashtra' }] },
  'financial-years': { label: 'Financial Years', icon: CalendarRange, path: 'financial-years', parent: true, singular: 'Financial Year',
    fields: [{ k: 'name', label: 'FY name', placeholder: '2026-27' }, { k: 'start_date', label: 'Start date', type: 'date' }, { k: 'end_date', label: 'End date', type: 'date' }] },
};

export const Masters = ({ sub = 'companies' }) => {
  const [tab, setTab] = useState(TABS[sub] ? sub : 'companies');
  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const cfg = TABS[tab];

  useEffect(() => { if (TABS[sub]) setTab(sub); }, [sub]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, r] = await Promise.all([erpList('companies'), erpList(cfg.path)]);
      setCompanies(cr.data.items || []); setRows(r.data.items || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [cfg.path]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await erpCreate(cfg.path, form); toast.success(`${cfg.singular} added`); setOpen(false); setForm({}); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const companyName = (id) => companies.find((c) => c.id === id)?.name || '—';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const columns = [
    { key: 'name', label: 'Name', render: (r) => <span className="font-semibold">{r.name}</span> },
    ...(cfg.parent ? [{ key: 'company_id', label: 'Company', render: (r) => companyName(r.company_id) }] : []),
    ...(tab === 'financial-years' ? [{ key: 'period', label: 'Period', render: (r) => `${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}` }] : [{ key: 'state', label: 'State', render: (r) => r.state || '—' }]),
    { key: 'is_active', label: 'Status', render: (r) => <Badge tone={r.is_active === false ? 'gray' : 'green'}>{r.is_active === false ? 'Inactive' : 'Active'}</Badge> },
    { key: 'created_at', label: 'Created', render: (r) => <span style={{ color: 'var(--erp-muted)' }}>{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div data-testid="erp-masters">
      <PageHeader crumb="Masters" title="Organisation Setup" subtitle="Companies, branches and financial years that anchor every transaction."
        actions={<Button icon={Plus} onClick={() => { setForm({}); setOpen(true); }} data-testid="masters-add">New {cfg.singular}</Button>} />
      <div className="mb-4"><Segmented testid="masters-tab" value={tab} onChange={setTab} options={Object.entries(TABS).map(([v, t]) => ({ value: v, label: t.label }))} /></div>
      <Card>
        <Table testid="masters-table" columns={columns} rows={rows} loading={loading}
          empty={<EmptyState icon={cfg.icon} title={`No ${cfg.label.toLowerCase()} yet`} text={`Add your first ${cfg.singular.toLowerCase()} to get started.`} action={<Button icon={Plus} onClick={() => setOpen(true)}>New {cfg.singular}</Button>} />} />
      </Card>

      <FormModal open={open} onClose={() => setOpen(false)} onSubmit={save} busy={busy} testid="masters-modal" title={`New ${cfg.singular}`} subtitle={`Add a ${cfg.singular.toLowerCase()} to the organisation.`}>
        {cfg.parent && (
          <Select label="Company" required value={form.company_id || ''} onChange={set('company_id')} data-testid="masters-company-select">
            <option value="">Select company</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          {cfg.fields.map((f, i) => (
            <Input key={f.k} label={f.label} type={f.type || 'text'} required={i === 0 || f.type === 'date'} placeholder={f.placeholder}
              value={form[f.k] || ''} onChange={set(f.k)} data-testid={`masters-field-${f.k}`} className={i === 0 ? 'sm:col-span-2' : ''} />
          ))}
        </div>
      </FormModal>
    </div>
  );
};
